import { type MouseEvent, useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router";
import DatagridV2 from "ui-kit/src/Datagrid/DatagridV2";
import {
	subscriptionsColumns,
	subscriptionsFilters,
	subscriptionsBackendAdapter,
	type SubscriptionRow,
} from "../datagrid-configs/subscriptions";
import type { ColumnConfig } from "../datagrid-configs/types";
import { ColumnVisibilityModal } from "../datagrid/ColumnVisibilityModal";
import { DatagridFilters } from "../datagrid/DatagridFilters";
import { useOperationsManagementTable } from "../operationsManagement/hooks/useOperationsManagementTable";
import { useSubscriptionFilterOptions } from "./hooks/useSubscriptionFilterOptions";
import client from "libs/client";
import { _ } from "i18n";
import { APIResponse } from "types";
import { useToast } from "ui-kit";
import Modal from "ui-kit/src/Modal";
import AssignSubscriptionModal, {
	type AssignSubscriptionClubSelection,
} from "pages/productManagement/subscriptionManagement/AssignSubscriptionModal";
import UnAssignSubscriptionModal from "pages/productManagement/subscriptionManagement/UnAssignSubscriptionModal";
import { getApiErrorMessage, parseMessageAttribute } from "libs/utils";
import "../operationsManagement/OperationsManagementSummaryTableV3.css";

interface SubscriptionsTableProps {
	storageKey?: string;
}

export function SubscriptionsTable({ storageKey = "subscriptions" }: SubscriptionsTableProps) {
	const { dynamicOptions } = useSubscriptionFilterOptions(subscriptionsFilters);
	const navigate = useNavigate();
	const { addSuccessToast, addErrorToast } = useToast();
	const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionRow | null>(null);
	const [showAssignSubscriptionModal, setShowAssignSubscriptionModal] = useState(false);
	const [showUnAssignSubscriptionModal, setShowUnAssignSubscriptionModal] = useState(false);

	const handleRowAction = useCallback(
		(row: SubscriptionRow, event: React.MouseEvent<HTMLTableRowElement>) => {
			const selectedText = window.getSelection();
			const hasSelectedRowText =
				selectedText !== null &&
				selectedText.toString().trim() !== "" &&
				selectedText.anchorNode !== null &&
				selectedText.focusNode !== null &&
				event.currentTarget.contains(selectedText.anchorNode) &&
				event.currentTarget.contains(selectedText.focusNode);

			if (hasSelectedRowText) {
				return;
			}

			navigate(`/subscription/${row.id}`);
		},
		[navigate],
	);

	const handleAssignmentClick = useCallback((event: MouseEvent<HTMLButtonElement>, row: SubscriptionRow) => {
		event.stopPropagation();
		setSelectedSubscription(row);

		if (row.proClubId != null) {
			setShowUnAssignSubscriptionModal(true);
		} else {
			setShowAssignSubscriptionModal(true);
		}
	}, []);

	const tableColumns = useMemo<ColumnConfig<SubscriptionRow>[]>(
		() => [
			...subscriptionsColumns,
			{
				key: "proClubId",
				label: _("label.assign"),
				sortable: false,
				cell: (_value, row) => (
					<button
						type="button"
						className={row.proClubId != null ? "assign-btn assigned-btn" : "assign-btn"}
						onClick={(event) => handleAssignmentClick(event, row)}
					>
						{row.proClubId != null ? _("label.unassign") : _("label.assign")}
					</button>
				),
				group: "Overview",
				defaultVisible: true,
				width: 120,
			},
		],
		[handleAssignmentClick],
	);

	const {
		pagination,
		setPagination,
		sort,
		handleSort,
		filterValues,
		filtersCollapsed,
		handleFilterChange,
		handleClearFilters,
		handleToggleFiltersCollapse,
		visibleColumns,
		defaultVisibility,
		columnOrder,
		defaultColumnOrder,
		showColumnModal,
		setShowColumnModal,
		handleVisibilityChange,
		groupColors,
		columnGroups,
		apiFilters,
		apiSort,
		simpleColumns,
		columnWidths,
		handleColumnWidthsChange,
	} = useOperationsManagementTable<SubscriptionRow>(
		{
			columns: tableColumns,
			filters: subscriptionsFilters,
			backendAdapter: subscriptionsBackendAdapter,
			storageKey,
			defaultPageSize: 50,
		},
		[],
	);

	const queryParams = useMemo(
		() => ({
			page: pagination.pageIndex + 1,
			limit: pagination.pageSize,
			filters: apiFilters.length > 0 ? apiFilters : undefined,
			order: apiSort ? { field: apiSort.field, direction: apiSort.direction as "ASC" | "DESC" } : undefined,
		}),
		[pagination.pageIndex, pagination.pageSize, apiFilters, apiSort],
	);

	const { data: subscriptionsResponse, isLoading, isError, refetch } = client.getSubscriptions.useQuery(queryParams, {
		retry: false,
	});

	const subscriptionsData = subscriptionsResponse?.data;
	const tableData = useMemo(() => subscriptionsData?.items ?? [], [subscriptionsData]);

	const pageCount = subscriptionsData?.pages ?? 0;
	const handleRetry = useCallback(() => refetch(), [refetch]);
	const handleCloseAssignSubscriptionModal = useCallback(() => {
		setShowAssignSubscriptionModal(false);
	}, []);

	const handleCloseUnAssignSubscriptionModal = useCallback(() => {
		setShowUnAssignSubscriptionModal(false);
	}, []);

	const handleAssignSubscription = useCallback(
		async (values: AssignSubscriptionClubSelection) => {
			if (selectedSubscription === null) {
				return;
			}

			const rawClubId = values?.value;
			if (rawClubId == null) {
				return;
			}

			const clubId = typeof rawClubId === "number" ? rawClubId : Number(rawClubId);
			if (Number.isFinite(clubId) === false) {
				return;
			}

			try {
				const response = await client.assignSubscriptionToClub.mutation({
					clubId,
					subscriptionId: selectedSubscription.id,
				});

				if (response?.data.status === APIResponse.SUCCESS) {
					if (response.data.message != null) {
						addSuccessToast(response.data.message);
					}
					setShowAssignSubscriptionModal(false);
					void refetch();
				} else if (response?.data.message != null) {
					addErrorToast(response.data.message);
				}
			} catch (error: unknown) {
				addErrorToast(getApiErrorMessage(parseMessageAttribute(error)));
			}
		},
		[addErrorToast, addSuccessToast, refetch, selectedSubscription],
	);

	const handleUnAssignSubscription = useCallback(async () => {
		if (selectedSubscription === null || selectedSubscription.proClubId == null) {
			return;
		}

		try {
			const response = await client.assignSubscriptionToClub.mutation({
				clubId: selectedSubscription.proClubId,
				subscriptionId: selectedSubscription.id,
				unAssign: 1,
			});

			if (response?.data.status === APIResponse.SUCCESS) {
				if (response.data.message != null) {
					addSuccessToast(response.data.message);
				}
				setShowUnAssignSubscriptionModal(false);
				void refetch();
			} else if (response?.data.message != null) {
				addErrorToast(response.data.message);
			}
		} catch (error: unknown) {
			addErrorToast(getApiErrorMessage(parseMessageAttribute(error)));
		}
	}, [addErrorToast, addSuccessToast, refetch, selectedSubscription]);

	return (
		<div className="discover-card discover-card--for-table">
			<div className="operationsManagementSummaryTableV3">
				<div className={`operationsManagementSummaryTableV3-header ${filtersCollapsed ? "filters-collapsed" : ""}`}>
					{!filtersCollapsed && (
						<div className="operationsManagementSummaryTableV3-headerTop">
							<div className="operationsManagementSummaryTableV3-headerLeft">
								<DatagridFilters
									filters={subscriptionsFilters}
									filterValues={filterValues}
									onFilterChange={handleFilterChange}
									onClear={handleClearFilters}
									isCollapsed={filtersCollapsed}
									onToggleCollapse={handleToggleFiltersCollapse}
									dynamicOptions={dynamicOptions}
									columnVisibility={visibleColumns}
								/>
							</div>
						</div>
					)}

					{filtersCollapsed && (
						<div className="operationsManagementSummaryTableV3-headerSingleRow">
							<div className="operationsManagementSummaryTableV3-headerLeft">
								<DatagridFilters
									filters={subscriptionsFilters}
									filterValues={filterValues}
									onFilterChange={handleFilterChange}
									onClear={handleClearFilters}
									isCollapsed={filtersCollapsed}
									onToggleCollapse={handleToggleFiltersCollapse}
									dynamicOptions={dynamicOptions}
									columnVisibility={visibleColumns}
								/>
							</div>
							<div className="operationsManagementSummaryTableV3-headerRight">
								<button
									type="button"
									className="operationsManagementSummaryTableV3-columnBtn"
									onClick={() => setShowColumnModal(true)}
									title="Configure column visibility"
								>
									Column visibility
								</button>
							</div>
						</div>
					)}

					{!filtersCollapsed && (
						<div className="operationsManagementSummaryTableV3-headerBottom">
							<button
								type="button"
								className="operationsManagementSummaryTableV3-columnBtn"
								onClick={() => setShowColumnModal(true)}
								title="Configure column visibility"
							>
								Column visibility
							</button>
						</div>
					)}
				</div>

				<div className="operationsManagementSummaryTableV3-table">
					<DatagridV2<SubscriptionRow>
						noResults="No subscriptions found"
						data={tableData}
						simpleColumns={simpleColumns}
						pagination={pagination}
						setPagination={setPagination}
						pageCount={pageCount}
						isLoading={isLoading}
						isError={isError}
						onRetry={handleRetry}
						columnVisibility={visibleColumns}
						showPageSizeSelector
						totalItems={subscriptionsData?.total}
						onColumnWidthsChange={handleColumnWidthsChange}
						autoSizeColumns
						stickyHeader
						rowAction={handleRowAction}
					/>
				</div>

				{showColumnModal && (
					<ColumnVisibilityModal
						columnGroups={columnGroups}
						columnVisibility={visibleColumns}
						defaultVisibility={defaultVisibility}
						onVisibilityChange={handleVisibilityChange}
						onClose={() => setShowColumnModal(false)}
						columnOrder={columnOrder}
						defaultColumnOrder={defaultColumnOrder}
						customViews={[]}
						selectedCustomViewId={null}
						onCustomViewsChange={() => undefined}
						groupColors={groupColors}
					/>
				)}
			</div>
			<Modal
				show={showAssignSubscriptionModal}
				title={_("label.assign.subscription.club")}
				onClose={handleCloseAssignSubscriptionModal}
				size="sm"
			>
				<AssignSubscriptionModal
					onClose={handleCloseAssignSubscriptionModal}
					onAssignSubscription={handleAssignSubscription}
				/>
			</Modal>
			<Modal
				show={showUnAssignSubscriptionModal}
				title={`${_("label.unassign")} ${selectedSubscription?.proClubName ?? ""}`}
				onClose={handleCloseUnAssignSubscriptionModal}
				size="sm"
			>
				<UnAssignSubscriptionModal
					onClose={handleCloseUnAssignSubscriptionModal}
					onUnAssign={handleUnAssignSubscription}
				/>
			</Modal>
		</div>
	);
}

export default SubscriptionsTable;
